import { formatLargeNumber, formatPercentage } from '@/helpers/utils.ts'
import { createNeighborhoodTooltipElement } from '../components/neighborhood-tooltip'
import { NeighborhoodStatsSchema, type Subgeo } from '../types'
import type { NeighborhoodData } from './leaflet-map-utils.ts'

const numberFormatter = new Intl.NumberFormat('en-US')

export interface SubgeoAggregates {
	gbv_total: number
	nights_total: number
	occupancy_avg: number
}

export interface TooltipCache {
	get: (id: string) => HTMLDivElement | undefined
	set: (id: string, element: HTMLDivElement) => void
	clear: () => void
	has: (id: string) => boolean
	deleteBySubgeoId: (
		subgeoId: string,
		subgeoLookup: Map<string, Subgeo>
	) => void
}

export interface AggregateCache {
	get: (subgeoId: string) => SubgeoAggregates | undefined
	set: (subgeoId: string, aggregates: SubgeoAggregates) => void
	clear: () => void
	delete: (subgeoId: string) => void
}

/**
 * Creates a simple cache for tooltip DOM elements.
 */
export const createTooltipCache = (): TooltipCache => {
	const cache = new Map<string, HTMLDivElement>()

	return {
		get: (id: string) => cache.get(id),
		set: (id: string, element: HTMLDivElement) => cache.set(id, element),
		clear: () => cache.clear(),
		has: (id: string) => cache.has(id),
		deleteBySubgeoId: (
			subgeoId: string,
			subgeoLookup: Map<string, Subgeo>
		) => {
			const keysToDelete: string[] = []
			for (const [neighborhoodId, subgeo] of subgeoLookup.entries()) {
				if (subgeo.id === subgeoId) {
					keysToDelete.push(neighborhoodId)
				}
			}
			for (const key of keysToDelete) {
				cache.delete(key)
			}
		}
	}
}

export const createAggregateCache = (): AggregateCache => {
	const cache = new Map<string, SubgeoAggregates>()

	return {
		get: (subgeoId: string) => cache.get(subgeoId),
		set: (subgeoId: string, aggregates: SubgeoAggregates) =>
			cache.set(subgeoId, aggregates),
		clear: () => cache.clear(),
		delete: (subgeoId: string) => cache.delete(subgeoId)
	}
}

const calculateSubgeoAggregates = (
	subgeo: Subgeo,
	neighborhoodDataMap: Map<string, NeighborhoodData>
): SubgeoAggregates => {
	let gbv_total = 0
	let nights_total = 0
	let weighted_occupancy_sum = 0
	let total_nights_for_occupancy = 0

	for (const neighborhoodId of subgeo.neighborhoodIds) {
		const data = neighborhoodDataMap.get(neighborhoodId)
		if (!data) {
			continue
		}

		const parsedStats = NeighborhoodStatsSchema.safeParse(data.stats)
		if (parsedStats.success && parsedStats.data) {
			gbv_total += parsedStats.data.gbv_prev_1yr
			nights_total += parsedStats.data.nights_stay_prev_1yr

			if (parsedStats.data.nights_stay_prev_1yr > 0) {
				weighted_occupancy_sum +=
					parsedStats.data.occupancy_prev_1yr *
					parsedStats.data.nights_stay_prev_1yr
				total_nights_for_occupancy +=
					parsedStats.data.nights_stay_prev_1yr
			}
		}
	}

	return {
		gbv_total,
		nights_total,
		occupancy_avg:
			total_nights_for_occupancy > 0
				? weighted_occupancy_sum / total_nights_for_occupancy
				: 0
	}
}

/**
 * Creates a factory function for tooltip creation with caching.
 * @param neighborhoodDataMap - Map of neighborhood IDs to data
 * @param subgeoLookup - Map of neighborhood IDs to subgeos
 * @param t - Translation function
 * @param tooltipCache - Tooltip cache instance
 * @param aggregateCache - Aggregate cache instance
 * @returns Function that creates/retrieves cached tooltips
 */
export const createTooltipFactory = (
	neighborhoodDataMap: Map<string, NeighborhoodData>,
	subgeoLookup: Map<string, Subgeo>,
	t: (key: string, options?: Record<string, string>) => string,
	tooltipCache: TooltipCache,
	aggregateCache: AggregateCache
) => {
	return (neighborhoodId: string): HTMLDivElement | null => {
		const cached = tooltipCache.get(neighborhoodId)
		if (cached) {
			return cached
		}

		const data = neighborhoodDataMap.get(neighborhoodId)
		if (!data) {
			return null
		}

		const subgeo = subgeoLookup.get(neighborhoodId)
		const parsedStats = NeighborhoodStatsSchema.safeParse(data.stats)

		const stats: Array<{ label: string; value: string | number }> = [
			{
				label: t('subgeo:stat_gbv'),
				value:
					parsedStats.success && parsedStats.data
						? formatLargeNumber(parsedStats.data.gbv_prev_1yr)
						: 'N/A'
			},
			{
				label: t('subgeo:stat_occupancy'),
				value:
					parsedStats.success && parsedStats.data
						? formatPercentage(parsedStats.data.occupancy_prev_1yr)
						: 'N/A'
			},
			{
				label: t('subgeo:stat_nights_stay'),
				value:
					parsedStats.success && parsedStats.data
						? numberFormatter.format(
								Math.round(
									parsedStats.data.nights_stay_prev_1yr
								)
							)
						: 'N/A'
			}
		]

		let subgeoStats:
			| Array<{ label: string; value: string | number }>
			| undefined
		if (subgeo) {
			let aggregates = aggregateCache.get(subgeo.id)

			if (!aggregates) {
				aggregates = calculateSubgeoAggregates(
					subgeo,
					neighborhoodDataMap
				)
				aggregateCache.set(subgeo.id, aggregates)
			}

			subgeoStats = [
				{
					label: t('subgeo:stat_gbv'),
					value: formatLargeNumber(aggregates.gbv_total)
				},
				{
					label: t('subgeo:stat_occupancy'),
					value: formatPercentage(aggregates.occupancy_avg)
				},
				{
					label: t('subgeo:stat_nights_stay'),
					value: numberFormatter.format(
						Math.round(aggregates.nights_total)
					)
				}
			]
		}

		const tooltipElement = createNeighborhoodTooltipElement({
			neighborhoodName: data.name,
			subgeoName: subgeo?.name,
			stats,
			subgeoStats
		})

		tooltipCache.set(neighborhoodId, tooltipElement)
		return tooltipElement
	}
}
